/**
 * Auth Service (Web)
 *
 * Owns: login, logout, session helpers, user creation (Firebase Auth side),
 * profile/credential updates (password, email, display name), and token refresh.
 *
 * Extracted from src/firebase.js per the service-decomposition plan
 * (docs/architecture/scalability-architecture.md §4). firebase.js retains
 * temporary re-export shims marked `// TODO remove shim — refactor:authService`.
 *
 * NOTE: createUser and updateUserProfile are compound operations that touch
 * both Firebase Auth and RTDB. They live here for now because their primary
 * concern is Auth-account lifecycle. When userService is extracted (next
 * refactor step), the RTDB portions should migrate there.
 *
 * Public API:
 *   forceRefreshClaim(): Promise<string>
 *   createUser(userData): Promise<object>
 *   updateUserProfile(userId, updateData): Promise<object>
 *
 * Usage notes for forceRefreshClaim:
 *   Call after a userCompanyMappings write or modification (e.g., onboarding
 *   completion, tenant switch) to ensure the Firebase Auth ID token reflects
 *   the latest custom claims (companyId, role) before making Storage or RTDB
 *   writes that are gated on those claims.
 *
 *   Identity-claim drift window: setCompanyClaim CF mints claims async after
 *   /userCompanyMappings/{uid} is written. The client token is refreshed on
 *   a ~1-hour schedule by the Firebase SDK. Without a forced refresh, Storage
 *   uploads made immediately after onboarding can fail because the token still
 *   carries the old tenant claim. Calling forceRefreshClaim() collapses this
 *   window to zero.
 *
 *   Example (onboarding complete handler):
 *     import { forceRefreshClaim } from '../services/authService';
 *     await writeUserCompanyMapping(uid, companyId, role);
 *     await forceRefreshClaim(); // ensures token has new companyId claim
 *     await uploadCompanyLogo(companyId, logoFile); // Storage rule reads claim
 */

import {
  createUserWithEmailAndPassword,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { ref, set, get, update } from 'firebase/database';
import { getAuthInstance, getFirebase } from '../firebase';
// NOTE: authService imports directly from '../firebase' (not via recruiterApi) to
// avoid a circular dependency: authService → recruiterApi → firebase → authService.
import { detectUserCompany } from '../utils/companyUtils';

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

/**
 * Force-refresh the Firebase Auth ID token so that the latest custom claims
 * (companyId, role) minted by the setCompanyClaim Cloud Function are present
 * in subsequent token-gated operations (Storage, RTDB).
 *
 * Callers should invoke this after userCompanyMappings is written or modified.
 * DO NOT wire this into existing auth flows — that is a frontend/mobile task.
 *
 * @returns {Promise<string>} the fresh ID token string
 * @throws if the user is not signed in or if the token refresh fails
 */
export async function forceRefreshClaim() {
  const auth = getAuthInstance();
  if (!auth || !auth.currentUser) {
    throw new Error('authService.forceRefreshClaim: no signed-in user — cannot refresh token');
  }
  // Pass true to bypass the SDK cache and force a server round-trip.
  return auth.currentUser.getIdToken(/* forceRefresh */ true);
}

/**
 * Create a new user in Firebase Auth and write user data to RTDB.
 *
 * NOTE: This is a compound operation (Auth + RTDB). The RTDB write portion
 * will migrate to userService when that service is extracted.
 *
 * @param {Object} userData
 * @param {string} userData.email
 * @param {string} userData.password
 * @param {string} userData.name
 * @param {string} userData.role
 * @param {string} [userData.companyId]
 * @param {string} [userData.companyName]
 * @param {string} [userData.status]
 * @param {string} [userData.qrCode]
 * @returns {Promise<object>} the stored user data object
 */
export const createUser = async (userData) => {
  const { auth, database } = getFirebase();
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );

    // Update user profile with display name
    await updateProfile(userCredential.user, {
      displayName: userData.name
    });

    // Determine storage path based on company - always use company-specific path
    const targetCompanyId = userData.companyId || 'legacy';
    const userPath = `companies/${targetCompanyId}/users/${userCredential.user.uid}`;

    // Create company mapping for all users
    const mappingRef = ref(database, `userCompanyMappings/${userCredential.user.uid}`);
    await set(mappingRef, {
      companyId: targetCompanyId,
      companyName: userData.companyName || 'Creative Personnel Technology',
      role: userData.role,
      joinedAt: new Date().toISOString()
    });

    let userDataToStore;
    const userRef = ref(database, userPath);
    userDataToStore = {
      id: userCredential.user.uid,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      status: userData.status || 'active',
      qrCode: userData.qrCode,
      createdAt: new Date().toISOString()
    };

    await set(userRef, userDataToStore);
    return userDataToStore;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update an existing user's profile data in both Firebase Auth and RTDB.
 *
 * Handles: display name, email (requires reauthentication), password (requires
 * reauthentication), and preferences. Sensitive operations require
 * updateData.currentPassword to be provided.
 *
 * NOTE: This is a compound operation (Auth + RTDB). The RTDB write portion
 * will migrate to userService when that service is extracted.
 *
 * @param {string} userId - The UID of the user to update
 * @param {Object} updateData
 * @param {string} [updateData.name]
 * @param {string} [updateData.email]
 * @param {string} [updateData.newPassword]
 * @param {string} [updateData.currentPassword] - Required for email/password changes
 * @param {Object} [updateData.preferences]
 * @returns {Promise<object>} the updated user data object
 */
export const updateUserProfile = async (userId, updateData) => {
  const { auth, database } = getFirebase();

  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }

    // Detect user's company to get correct path
    const companyInfo = await detectUserCompany(userId);
    const companyId = companyInfo ? companyInfo.companyId : 'legacy';

    // Get appropriate path for user - always use company-specific path
    const userPath = `companies/${companyId}/users/${userId}`;
    dbg('updateUserProfile - Using path:', userPath, 'for company:', companyId);

    // First, get the current user data from database
    const userRef = ref(database, userPath);
    const userSnapshot = await get(userRef);

    if (!userSnapshot.exists()) {
      throw new Error('User not found in database');
    }

    const currentUserData = userSnapshot.val();
    const updatedUserData = { ...currentUserData };

    // Check if we need to reauthenticate (for sensitive operations)
    const needsReauth = (updateData.newPassword || (updateData.email && updateData.email !== currentUser.email));
    let hasReauthenticated = false;

    // Handle reauthentication if needed
    if (needsReauth && updateData.currentPassword) {
      dbg('Reauthenticating user for sensitive operations...');

      try {
        const credential = EmailAuthProvider.credential(
          currentUser.email,
          updateData.currentPassword
        );

        await reauthenticateWithCredential(currentUser, credential);
        dbg('Reauthentication successful');
        hasReauthenticated = true;
      } catch (error) {
        console.error('Reauthentication failed:', error);
        if (error.code === 'auth/invalid-credential') {
          throw new Error('Current password is incorrect. Please check your password and try again.');
        }
        throw error;
      }
    }

    // Handle password update
    if (updateData.newPassword) {
      if (!hasReauthenticated) {
        throw new Error('Reauthentication required for password change');
      }

      dbg('Updating password...');
      await updatePassword(currentUser, updateData.newPassword);
      dbg('Password updated in Firebase Auth');
    }

    // Handle email update
    if (updateData.email && updateData.email !== currentUser.email) {
      if (!hasReauthenticated && updateData.currentPassword) {
        throw new Error('Reauthentication required for email change');
      }

      dbg('Updating email...');
      await updateEmail(currentUser, updateData.email);
      dbg('Email updated in Firebase Auth');

      // Update email in database
      updatedUserData.email = updateData.email;
    }

    // Handle name update (doesn't require reauthentication)
    if (updateData.name && updateData.name !== currentUserData.name) {
      dbg('Updating display name...');

      // Update display name in Firebase Auth
      await updateProfile(currentUser, {
        displayName: updateData.name
      });
      dbg('Display name updated in Firebase Auth');

      // Update name in database
      updatedUserData.name = updateData.name;
    }

    // Handle preferences update (doesn't require reauthentication)
    if (updateData.preferences) {
      dbg('Updating preferences...', updateData.preferences);
      updatedUserData.preferences = {
        ...(currentUserData.preferences || {}),
        ...updateData.preferences
      };
    }

    // Update the database with all changes
    updatedUserData.updatedAt = new Date().toISOString();
    await update(userRef, updatedUserData);
    dbg('Database updated successfully');

    return {
      id: userId,
      ...updatedUserData
    };

  } catch (error) {
    console.error('Error updating user profile:', error);

    // Provide more user-friendly error messages
    if (error.message === 'Current password is incorrect. Please check your password and try again.') {
      throw error; // Re-throw our custom message
    }

    // Handle other Firebase Auth errors
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error('Current password is incorrect. Please check your password and try again.');
    } else if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already in use by another account.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address format.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please choose a stronger password.');
    } else if (error.code === 'auth/requires-recent-login') {
      throw new Error('This operation requires recent authentication. Please log out and log back in, then try again.');
    }

    throw error;
  }
};
